<template>
  <div class="contract-attachments">
    <div v-if="!readonly" class="attachment-toolbar">
      <el-upload
        action="#"
        :show-file-list="false"
        :multiple="true"
        :http-request="handleUpload"
        :before-upload="beforeUpload"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
      >
        <el-button type="primary" size="small" icon="el-icon-upload2" :loading="uploading">
          上传附件
        </el-button>
      </el-upload>
      <span class="attachment-tip">支持 PDF、Word 和图片，单文件不超过 50MB</span>
    </div>

    <el-table v-loading="loading" :data="documents" border size="small" empty-text="暂无合同附件">
      <el-table-column prop="originalName" label="文件名称" min-width="200" show-overflow-tooltip />
      <el-table-column label="大小" width="90" align="center">
        <template slot-scope="scope">{{ formatFileSize(scope.row.fileSize) }}</template>
      </el-table-column>
      <el-table-column prop="uploaderName" label="上传人" width="110" align="center">
        <template slot-scope="scope">{{ scope.row.uploaderName || '--' }}</template>
      </el-table-column>
      <el-table-column label="上传时间" width="170" align="center">
        <template slot-scope="scope">{{ formatDateTime(scope.row.uploadTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" :width="readonly ? 80 : 130" align="center">
        <template slot-scope="scope">
          <el-button
            v-if="canDownload"
            type="text"
            size="mini"
            icon="el-icon-download"
            @click="download(scope.row)"
          >下载</el-button>
          <el-button
            v-if="!readonly && canDelete(scope.row)"
            type="text"
            size="mini"
            class="delete-button"
            @click="remove(scope.row)"
          >删除</el-button>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import {
  deleteBusinessDocument,
  downloadBusinessDocument,
  getContractAttachments,
  getTemporaryContractAttachments,
  uploadContractAttachment,
  uploadTemporaryContractAttachment
} from '@/api/modules/business-document'
import { getUserInfo } from '@/utils/auth'

const MAX_FILE_SIZE = 50 * 1024 * 1024

export default {
  name: 'ContractAttachments',
  props: {
    contractId: {
      type: [Number, String],
      default: null
    },
    uploadToken: {
      type: String,
      default: ''
    },
    readonly: {
      type: Boolean,
      default: false
    },
    downloadPermission: {
      type: String,
      default: 'contracts:contract-view:download-attachment'
    }
  },
  data() {
    return {
      documents: [],
      loading: false,
      uploadCount: 0
    }
  },
  computed: {
    uploading() {
      return this.uploadCount > 0
    },
    canDownload() {
      const permissionCodes = this.$store.state.permissionCodes || []
      return permissionCodes.includes(this.downloadPermission) ||
        permissionCodes.includes('contracts:contracts-list:download-scan')
    }
  },
  watch: {
    contractId: {
      immediate: true,
      handler() {
        this.load()
      }
    },
    uploadToken() {
      this.load()
    }
  },
  methods: {
    async load() {
      if (!this.contractId && !this.uploadToken) {
        this.documents = []
        this.emitCounts()
        return
      }
      this.loading = true
      try {
        const requests = []
        if (this.contractId) requests.push(getContractAttachments(this.contractId))
        if (this.uploadToken) requests.push(getTemporaryContractAttachments(this.uploadToken))
        const responses = await Promise.all(requests)
        const documentMap = new Map()
        responses.forEach(response => {
          const items = response.data && response.data.success ? response.data.data || [] : []
          items.forEach(item => documentMap.set(item.id, item))
        })
        this.documents = Array.from(documentMap.values()).sort((a, b) =>
          String(b.uploadTime || '').localeCompare(String(a.uploadTime || ''))
        )
        this.emitCounts()
      } catch (error) {
        this.$message.error(this.errorMessage(error, '附件加载失败'))
      } finally {
        this.loading = false
      }
    },
    emitCounts() {
      const temporaryCount = this.documents.filter(item => !item.contractId && item.uploadToken).length
      this.$emit('count-change', {
        total: this.documents.length,
        temporary: temporaryCount
      })
    },
    beforeUpload(file) {
      if (file.size > MAX_FILE_SIZE) {
        this.$message.error('单个文件不能超过 50MB')
        return false
      }
      if (!this.contractId && !this.uploadToken) {
        this.$message.error('附件上传会话尚未初始化')
        return false
      }
      return true
    },
    async handleUpload(options) {
      this.uploadCount++
      this.$emit('uploading-change', true)
      try {
        const response = this.contractId
          ? await uploadContractAttachment(this.contractId, options.file, options.onProgress)
          : await uploadTemporaryContractAttachment(this.uploadToken, options.file, options.onProgress)
        if (!response.data || !response.data.success) {
          throw new Error(response.data && response.data.message ? response.data.message : '附件上传失败')
        }
        options.onSuccess(response.data)
        this.$message.success('合同附件上传成功')
        await this.load()
      } catch (error) {
        options.onError(error)
        this.$message.error(this.errorMessage(error, '附件上传失败'))
      } finally {
        this.uploadCount--
        this.$emit('uploading-change', this.uploading)
      }
    },
    async download(item) {
      try {
        const response = await downloadBusinessDocument(item.id)
        this.saveBlob(response, item.originalName)
      } catch (error) {
        this.$message.error(this.errorMessage(error, '附件下载失败'))
      }
    },
    async remove(item) {
      try {
        await this.$confirm(`确定删除附件“${item.originalName}”吗？`, '提示', { type: 'warning' })
        const response = await deleteBusinessDocument(item.id)
        if (!response.data || !response.data.success) {
          throw new Error(response.data && response.data.message ? response.data.message : '附件删除失败')
        }
        this.$message.success('合同附件删除成功')
        await this.load()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error(this.errorMessage(error, '附件删除失败'))
        }
      }
    },
    canDelete(item) {
      const userInfo = getUserInfo() || {}
      const userId = userInfo.userId || userInfo.id || userInfo.user_id
      return userId != null && Number(userId) === Number(item.uploaderId)
    },
    saveBlob(response, fileName) {
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream'
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
    formatFileSize(size) {
      if (size == null) return '--'
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
      return `${(size / 1024 / 1024).toFixed(1)} MB`
    },
    formatDateTime(value) {
      return value ? String(value).replace('T', ' ').substring(0, 19) : '--'
    },
    errorMessage(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || error.message || fallback
    }
  }
}
</script>

<style scoped>
.attachment-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.attachment-tip {
  color: #909399;
  font-size: 12px;
}

.delete-button {
  color: #f56c6c;
}
</style>
